import React, { Dispatch, useEffect, useState } from "react";
import IconButton from "components/atoms/button/IconButton";
import { Controller, useForm } from "react-hook-form";
import Creatable from "react-select/creatable";
import PrimaryDatePicker from "components/molecules/datepicker/PrimaryDatePicker";
import { customStyles } from "lib/helpers/configs/customStyles";
import Label from "components/atoms/input/input-label/InputLabel";
import { usePartners } from "lib/hooks/queries/Partners/usePartnersForFilter";
import LoadingScreen from "components/atoms/loading-screen/LoadingScreen";
import { useTranslation } from "react-i18next";
import { getUsersByPermission } from "api/User";
import { usePermissions } from "lib/hooks/shared/usePermissions";
import ConditionalRender from "components/atoms/conditional-render/ConditionalRender";
import RemoveFIlter from "components/atoms/remove-filter/RemoveFIlter";

export interface IFilterProps {
  filter_search: string;
  filter_status: string;
  filter_product: string;
  filter_company: string;
  filter_commission: string;
  filter_mandated: string;
  customer_type: string;
  filter_created_at: any;
  filter_to_created_at: any;
  filter_from_user: any;
}

const initialState: IFilterProps = {
  filter_search: "",
  filter_status: "",
  filter_product: "",
  filter_company: "",
  filter_commission: "",
  filter_mandated: "",
  customer_type: "",
  filter_created_at: "",
  filter_to_created_at: "",
  filter_from_user: ""
};

export interface IFilterCostumersProps {
  setFilterData?: (args: any) => void;
  FilterData?: IFilterProps;
  setIsOpen?: Dispatch<React.SetStateAction<boolean>>;
  themeColors: {
    primary: string;
  };
}

const FilterCostumers: React.FC<IFilterCostumersProps> = ({
  setFilterData,
  FilterData,
  setIsOpen,
}) => {

  const { data: partnersData, isLoading } = usePartners();
  const [fetchedUsers, setFetchedUsers] = useState<Array<string>>([]);
  const hasPermissionToViewAll = usePermissions(['customers_view_view']);

  const {
    control,
    reset,
    watch,
    getValues,
    handleSubmit
  } = useForm({
    defaultValues: FilterData || initialState
  });

  const handleButtonClick = (data: any) => {

    setFilterData?.(data);
    setIsOpen?.(false);

  };

  useEffect(() => {
    getUsersByPermission({
      permissions: ['customers_view_view', 'customers_view_view_own'],
    })
      .then((res) => {
        setFetchedUsers(res.users);
      })
      .catch(() => { });
    // eslint-disable-next-line
  }, []);

  const ResetData = () => {
    reset(initialState);
  };

  const { t } = useTranslation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit(handleButtonClick)}>
      <Controller
        name="filter_created_at"
        control={control}
        render={({ field }) => (
          <PrimaryDatePicker
            label={t('From creation date')}
            setStartDate={field.onChange}
            startDate={field.value}
            wrapperClassName="!left-[2px]"
            DeleteData={() => reset({ ...getValues(), filter_created_at: null })}
            filterCondition={watch("filter_created_at")}
          />
        )}
      />
      <Controller
        name="filter_to_created_at"
        control={control}
        render={({ field }) => (
          <PrimaryDatePicker
            label={t('To creation date')}
            setStartDate={field.onChange}
            startDate={field.value}
            wrapperClassName="!left-[2px]"
            DeleteData={() => reset({ ...getValues(), filter_to_created_at: null })}
            filterCondition={watch("filter_to_created_at")}
          />
        )}
      />
      <div>
        <Label text={t('Status')} />
        <div className="relative">
          <Controller
            name="filter_status"
            control={control}
            render={({ field }) => (
              <Creatable<any, false>
                {...field}
                placeholder={t('Choose Status')}
                options={[
                  { value: "Open (Consultant)", label: t('Open (Consultant)') },
                  { value: "Open (Office)", label: t('Open (Office)') },
                  { value: "Submitted", label: t('Submitted') },
                  { value: "Accepted", label: t('Accepted') },
                  { value: "Provisioned", label: t('Provisioned') },
                  { value: "Rejected", label: t('Rejected') },
                  { value: "Retreat", label: t('Retreat') },
                  { value: "Canceled", label: t('Canceled') },
                  { value: "Quality", label: t('Quality') }
                ]}
                value={
                  field.value
                    ? {
                      value: field.value,
                      label: field.value
                    }
                    : null
                }
                className="cursor-pointer font-inter-regular react-select-container"
                classNamePrefix="react-select"
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption
                    ? selectedOption.value
                    : null;
                  field.onChange(selectedValue);

                }}
                styles={customStyles}
                isValidNewOption={() => false}
                menuPosition={"fixed"}
                menuPortalTarget={document.body}
              />
            )}
          />
          <RemoveFIlter
            condition={watch("filter_status")}
            DeleteData={() => {

              reset({ ...getValues(), filter_status: "" })
            }}
          />
        </div>
      </div>
      <ConditionalRender condition={hasPermissionToViewAll}>
        <div>
          <div>
            <Label>{t('Worker')}</Label>
          </div>
          <div className="relative">
            <Controller
              name="filter_from_user"
              control={control}
              render={({ field }) => (
                <Creatable<any, false>
                  {...field}
                  placeholder={t('Choose Worker')}
                  options={fetchedUsers?.map?.((source: any) => ({
                    label: `${source.name ?? ''} ${source.last_name ?? ''}`,
                    value: source.id,
                  }))}
                  value={
                    field?.value
                      ? {
                        value: field?.value,
                        label:
                          (
                            fetchedUsers?.find?.(
                              (item: any) =>
                                item.id === field.value
                            ) as { name: string } | undefined
                          )?.name || field.value,
                      }
                      : null
                  }
                  className="cursor-pointer font-inter-regular react-select-container"
                  classNamePrefix="react-select"
                  onChange={(newValue: any, actionMeta: any) => {
                    if (
                      actionMeta.action === "select-option" &&
                      newValue?.value
                    ) {
                      const selectedValue = newValue
                        ? newValue.value
                        : null;
                      field.onChange(selectedValue);

                    }
                  }}
                  styles={customStyles}
                  isValidNewOption={() => false}
                  menuPosition={"fixed"}
                  menuPortalTarget={document.body}
                />
              )}
            />
            <RemoveFIlter
              condition={watch("filter_from_user")}
              DeleteData={() => {

                reset({ ...getValues(), filter_from_user: "" })
              }}
            />
          </div>
          <div className="flex flex-col gap-[15px] 2xl:gap-[24px]"></div>
        </div>
      </ConditionalRender>

      <div>
        <Label text={t('Product')} />
        <div className="relative">


          <Controller
            name="filter_product"
            control={control}
            render={({ field }) => (
              <Creatable<any, false>
                {...field}
                placeholder={t('Choose Product')}
                options={[
                  { label: t("KVG"), value: "KVG" },
                  { label: t("VVG"), value: "VVG" },
                  { label: t("Life"), value: "Life" },
                  { label: t("Lawprotection"), value: "Lawprotection" },
                  { label: t("Business"), value: "Business" },
                  { label: t("Auto"), value: "Auto" },
                  { label: t("Company"), value: "Company" },
                ]}
                value={
                  field.value
                    ? {
                      value: field.value,
                      label: field.value
                    }
                    : null
                }
                className="cursor-pointer font-inter-regular react-select-container"
                classNamePrefix="react-select"
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption
                    ? selectedOption.value
                    : null;
                  field.onChange(selectedValue);

                }}
                styles={customStyles}
                isValidNewOption={() => false}
                menuPosition={"fixed"}
                menuPortalTarget={document.body}
              />
            )}
          />
          <RemoveFIlter
            condition={watch("filter_product")}
            DeleteData={() => {

              reset({ ...getValues(), filter_product: "" })
            }}
          />
        </div>
      </div>

      <div>
        <Label text={t('Company')} />
        <div className="relative">


          <Controller
            name="filter_company"
            control={control}
            render={({ field }) => {
              const options = partnersData?.partners?.map?.((partner: any) => ({
                label: partner?.name,
                value: partner?.name,
              })) || [];
              return (
                <Creatable<any, false>
                  {...field}
                  placeholder={t('Choose Company')}
                  options={options}
                  value={
                    field.value
                      ? {
                        value: field.value,
                        label: field.value
                      }
                      : null
                  }
                  className="cursor-pointer font-inter-regular react-select-container"
                  classNamePrefix="react-select"
                  onChange={(selectedOption) => {
                    const selectedValue = selectedOption
                      ? selectedOption.value
                      : null;
                    field.onChange(selectedValue);

                  }}
                  styles={customStyles}
                  isValidNewOption={() => false}
                  menuPosition={"fixed"}
                  menuPortalTarget={document.body}
                />
              );
            }}
          />
          <RemoveFIlter
            condition={watch("filter_company")}
            DeleteData={() => {

              reset({ ...getValues(), filter_company: "" })
            }}
          />
        </div>
      </div>

      <div>
        <Label text={t('Mandated')} />
        <div className="relative">


          <Controller
            name="filter_mandated"
            control={control}
            render={({ field }) => (
              <Creatable<any, false>
                {...field}
                placeholder={t('Choose Mandated')}
                options={[
                  { label: t('Yes'), value: "yes" },
                  { label: t('No'), value: "no" }
                ]}
                value={
                  field.value
                    ? {
                      value: field.value,
                      label: field.value === 'yes' ? t("Yes") : t('No')
                    }
                    : null
                }
                className="cursor-pointer font-inter-regular react-select-container"
                classNamePrefix="react-select"
                onChange={(selectedOption) => {

                  const selectedValue = selectedOption
                    ? selectedOption.value
                    : null;

                  const booleanValue = selectedValue === "yes" ? 1 : selectedValue === "no" ? 0 : null;
                  field.onChange(booleanValue);

                }}
                styles={customStyles}
                isValidNewOption={() => false}
                menuPosition={"fixed"}
                menuPortalTarget={document.body}
              />
            )}
          />
          <RemoveFIlter
            condition={watch("filter_mandated")}
            DeleteData={() => {

              reset({ ...getValues(), filter_mandated: "" })
            }}
          />
        </div>
      </div>
      <div>
        <Label text={t('Type')} />
        <div className="relative">
          <Controller
            name="customer_type"
            control={control}
            render={({ field }) => (
              <Creatable<any, false>
                {...field}
                placeholder={t('Choose Type')}
                options={[
                  { value: "Private", label: t('Private') },
                  { value: "Company", label: t('Company') },
                ]}
                value={
                  field.value
                    ? {
                      value: field.value,
                      label: t(field.value)
                    }
                    : null
                }
                className="cursor-pointer font-inter-regular react-select-container"
                classNamePrefix="react-select"
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption
                    ? selectedOption.value
                    : null;
                  field.onChange(selectedValue);

                }}
                styles={customStyles}
                isValidNewOption={() => false}
                menuPosition={"fixed"}
                menuPortalTarget={document.body}
              />
            )}
          />
          <RemoveFIlter
            condition={watch("customer_type")}
            DeleteData={() => {

              reset({ ...getValues(), customer_type: "" })
            }}
          />
        </div>
      </div>

      {/* <Input
        label={t('Provision')}
        name="filter_commission"
        type="number"
        placeholder="0.00"
        onChange={(e) => {
          const { name, value } = e.target;
          setInsideData((prevFormData: IFilterProps) => ({
            ...prevFormData,
            [name]: value
          }));
        }}
        value={InsideData.filter_commission}
      /> */}

      <div className="pt-5">
        <div className="flex flex-row justify-end gap-3">
          <IconButton
            secondary={true}
            className="w-[110px]"
            onClick={ResetData}
            type="button"
          >
            {t('Reset')}
          </IconButton>
          <IconButton className="w-[110px]">
            {t('Confirm')}
          </IconButton>
        </div>
      </div>
    </form>
  );
};

export default FilterCostumers;